import { useContext, useEffect, useState } from "react";

import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import AuthContext from "../../../../../shared/context/auth-context";
import { IDataType, IEnumerationOption } from "../../../../../shared/definitions/datatypes";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { getAllData, getData } from "../../../../../shared/utils/api";
import { backendDataTypeToFrontend, IObject, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ColumnSelector from "../../../column-selector/column-selector";
import { IColumn } from "../../../data-table/data-table";
import SearchInput from "../../../search-input/search-input";
import { IWidgetModalProps } from "../../widget-utils";
import { ITableWidget } from "../../widgets/table-widget/table-widget";
import Spinner from "@/shared/components/spinner/spinner";

export interface IDatamodelTableModal {
  type: "datamodelTable";
}

const DatamodelTableModal = ({modalOpen, widgetData, onWidgetChanged}: IWidgetModalProps<ITableWidget>) => {
  const authCtx = useContext(AuthContext);
  const [showModal, setShowModal] = useState(modalOpen || false);
  const [datamodelOptions, setDatamodelOptions] = useState<{id: string, name: string, datamodelType: string}[]>([]);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>([]);
  const [selectedDatamodel, setSelectedDatamodel] = useState<IEnumerationOption | undefined>();
  
  const { sendRequest: getDatamodels, data: getDatamodelData, status: getDatamodelStatus } = useHttp(getAllData, false, authCtx.token, undefined, "platform");
  const { sendRequest: getDatamodelInfo, data: getDatamodelInfoData, status: getDatamodelInfoStatus, error: getDatamodelInfoError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");

  useEffect(() => {
    if (widgetData?.id) {
      setSelectedDatamodel({id: widgetData.modalData?.id, name: widgetData.modalData?.label});
      setSelectedColumns(widgetData.columns);
    }
  }, [widgetData]);

  useEffect(() => {
    setShowModal(modalOpen || false);
  }, [modalOpen]);

  useEffect(() => {
    getDatamodels("list_datamodels?link=false");
  }, []);

  useEffect(() => {
    if (getDatamodelStatus === "completed") {
      setDatamodelOptions(getDatamodelData?.rows?.map((datamodel: IObject) => ({
        id: datamodel.id,
        name: datamodel.name,
        datamodelType: datamodel.type
      })) || []);
    }
  }, [getDatamodelStatus]);

  useEffect(() => {
    if (getDatamodelInfoStatus === "completed" && getDatamodelInfoData && !getDatamodelInfoError) {
      setColumns(getDatamodelInfoData.fields.map((field : IObject) => ({
        key: field.input_field_name,
        displayName: field.field_name,
        dataType: backendDataTypeToFrontend(field.field_datatype)
      })));
    }
  }, [getDatamodelInfoStatus, getDatamodelInfoData, getDatamodelInfoError]);

  const handleOnDatamodelChange = (newType: IEnumerationOption | undefined) => {
    setSelectedDatamodel(newType);
    if (!newType) {
      setSelectedColumns([]);
      setSelectedDatamodel(undefined);
      return;
    }
  };

  const handleOnSelectionChanged = (selectedColumns: IColumn[]) => {
    setSelectedColumns(selectedColumns);
  };

  const renderModalBody = () => {
    return <div>
      <SearchInput
        label="Datamodel type"
        handleOnChange={handleOnDatamodelChange}
        data={datamodelOptions}
        inputKey="datamodelType"
        required={true}
        selectedOption={selectedDatamodel?.id || ""}
      />
      {selectedDatamodel && (
        <div className="datamodelTableModal-columnSelector">
          <ColumnSelector availableColumns={columns} selectedColumns={selectedColumns} onSelectionChanged={handleOnSelectionChanged}/>
        </div>
      )}
    </div>;
  };

   useEffect(() => {
    if (!selectedDatamodel) {
      return;
    }
    const datamodel = datamodelOptions.find(option => option.id === selectedDatamodel.id);
    if (!datamodel) {
      return;
    }
    getDatamodelInfo(`datamodel/${datamodel.datamodelType}/${datamodel.id}`);
  }, [selectedDatamodel, datamodelOptions, getDatamodelInfo]);

  const [tableColumns, setTableColumns] = useState<IColumn[]>([]);
  useEffect(() => {
    if (getDatamodelInfoStatus === "completed" && getDatamodelInfoData) {
      setTableColumns(
        Object.entries(getDatamodelInfoData || {}).map(([key, value]: [string, unknown]) => ({
          key,
          displayName: value as string,
          dataType: "text" as unknown as IDataType,
          sortable: false
        }))
      );
    }
  }, [getDatamodelInfoStatus, getDatamodelInfoData]);

  const handleModalSubmit = () => {
    if (!tableColumns || tableColumns.length === 0) {
      return;
    }
  
    const datamodel = datamodelOptions.find(option => option.id === selectedDatamodel?.id);
    if (!datamodel) {
      return;
    }
  
    onWidgetChanged({
      id: widgetData?.id || `datamodelTable_${selectedDatamodel?.id}`,
      type: "table",
      modalType: "datamodelTable",
      label: selectedDatamodel?.name || "",
      columns: selectedColumns,
      dataUrl: {
        url: "datamodel_association",
        payload: {
          datamodel_type: datamodel?.datamodelType || "",
          datamodel_id: datamodel?.id || "",
          columns: selectedColumns.map(column => column.key)
        },
        transformType: "datamodelAssociation",
        backend: "manager"
      },
      modalData: {
        id: datamodel?.id || "",
        datamodelType: datamodel?.datamodelType || ""
      }
    });
  
    setShowModal(false);
  };

  const handleModalClosed = () => {
    onWidgetChanged(undefined);
    setShowModal(false);
  };

  const handleAddDatamodelTableClick = () => {
    setShowModal(true);
    setSelectedColumns([]);
    setSelectedDatamodel(undefined);
  }; 

  return <div className="datamodelTableModal">
    {validateLoadingStatusNew([getDatamodelStatus, getDatamodelInfoStatus]) && <Spinner />}
    <span className="datamodelFieldModal-layoutButton" onClick={handleAddDatamodelTableClick}>
      Datamodel table +
    </span>
    <SimpleModal allowScrollOnBody={false} mode="Datamodel table" onModalClickHandler={noop} modalBody={renderModalBody()} buttonText={"Add"} handleModalClosed={handleModalClosed} handleSubmit={handleModalSubmit} show={showModal}/>
  </div>;
};

export default DatamodelTableModal;